import logger from "@/lib/logger";
import { ClientFactory } from "@/main/common/ClientFactory";
import { queueSearchResultClicked } from "@/main/analytics/analyticsEvents";
import type { SearchResultBeaconContext } from "@/main/common/SearchBeaconTypes";

/**
 * The one place a search-result click turns into measurement.
 *
 * <p>Two things have to happen on that click and they answer different questions, so wiring only one
 * of them is the failure this module exists to prevent:
 *
 * <ul>
 *   <li>the GA4 event — aggregate click-through and click position, but blind to consent-decliners
 *       and ad-blockers, and it can never carry the query (`search_term` is blocked in gtm.ts);</li>
 *   <li>the SEARCH_RESULT_CLICKED activity row — first-party, covers everybody including anonymous
 *       visitors, and carries the QUERY, which is what makes per-query click-through possible at all.
 *       Nothing else could supply that join: `corr_id` is per-request and `discussion_views` has a
 *       `session_id` that activity rows do not.</li>
 * </ul>
 *
 * <p>Routing both through `onSearchResultActivated` means no call site can wire up half of it.
 * Design: designs/01-now/2026-08-01-search-success-instrumentation-strong-matches-and-result-clicks-design.md.
 */

/** The five search verticals, matching the server's `entity_type` values exactly. */
export type SearchResultVertical = "discussions" | "arguments" | "groups" | "series" | "users";

/**
 * What a search result row knows about how it was found. Built once per rendered vertical by
 * `SearchResultsPage`; `position` is filled in per row from the list's own render index.
 */
export type SearchResultAttribution = {
    /** The query as typed. Sent to our own store only — never to GA4. */
    query: string;
    entityType: SearchResultVertical;
    /** 1-based slot within the loaded list. */
    position: number;
    // `entityPublicId` (the clicked row's public id; a member's numeric id on the users vertical) rides
    // in with the beacon context below. First-party only: `queueSearchResultClicked` never sees it.
} & SearchBeaconContext;

/**
 * The SV-131 S4 search context (calibration design §4.1, §4.3), sent on the first-party beacons ONLY.
 * Every field is optional: the server validates each one and drops what fails, so a missing value is
 * sent as missing, never as a stand-in. `searchId` / `prevSearchId` come from
 * `@/main/analytics/searchSession`; `resolvedTab` is the tab the click happened on and `preferredTab`
 * the tab the visitor asked for, which is how the resolver's effect is measured after the fact.
 */
export type SearchBeaconContext = SearchResultBeaconContext;

/**
 * Call from the row's activation handler — in practice `SearchResultRowBase`'s full-row overlay link,
 * which is the row's single navigation path. Never from a container's `onClickCapture`, which would
 * also fire for the author link and for the sensitive-content reveal button.
 *
 * <p>Analytics is strictly side-effect-only: nothing here may throw into the navigation it rides on,
 * and the beacon is deliberately not awaited — the click's whole value is getting the user where they
 * clicked.
 */
export function onSearchResultActivated(attribution: SearchResultAttribution): void {
    const query = attribution.query?.trim() ?? "";
    if (!query) {
        // A blank query is a discovery list, not a search: there is no query for the click to attribute
        // to, and recording one would put empty-string rows in the middle of the top-queries report.
        return;
    }

    try {
        queueSearchResultClicked({
            entityType: attribution.entityType,
            position: attribution.position,
        });
    } catch (error) {
        logger.debug("[analytics] Failed to track the search result click.", error);
    }

    try {
        ClientFactory.postSearchResultClicked(query, attribution.entityType, attribution.position,
            beaconContextOf(attribution))
            .catch((error) => {
                // Best-effort by design. A dropped beacon costs one row of analytics; a rejected
                // promise escaping here would be an unhandled rejection on every navigation.
                logger.debug("[analytics] The search result click beacon did not reach the server.", error);
            });
    } catch (error) {
        logger.debug("[analytics] Failed to send the search result click beacon.", error);
    }
}

/**
 * Only the context keys, only when present: the beacon body must not carry `undefined` stand-ins
 * (Axios would drop them anyway, but a test reading the call must see exactly what was known).
 */
function beaconContextOf(attribution: SearchResultAttribution): SearchResultBeaconContext {
    const context: SearchResultBeaconContext = {};
    if (attribution.searchId) context.searchId = attribution.searchId;
    if (attribution.prevSearchId) context.prevSearchId = attribution.prevSearchId;
    if (attribution.uiLocale) context.uiLocale = attribution.uiLocale;
    if (attribution.languageScope) context.languageScope = attribution.languageScope;
    if (attribution.resolvedTab) context.resolvedTab = attribution.resolvedTab;
    if (attribution.preferredTab) context.preferredTab = attribution.preferredTab;
    if (attribution.entityPublicId) context.entityPublicId = attribution.entityPublicId;
    return context;
}
